/**
 * LenderMarketplaceLanding — public /lenders page.
 *
 * Sales-focused landing for loan officers and lending institutions, pitching
 * them on joining the LoanKnow marketplace. Captures interest into
 * lender_marketplace_signups so business development can follow up.
 *
 * NOTE: Distinct from `src/pages/lender/LenderMarketplace.jsx`, which is the
 * authenticated portal where onboarded lenders see live borrower files.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ArrowRight, CheckCircle, Sparkles, FileText, Target, Shield,
  TrendingUp, Users, Zap, AlertCircle, MapPin, Award, ChevronDown,
  ChevronUp, X,
} from 'lucide-react';
import { supabase, isSupabaseConfigured, friendlyErrorMessage } from '../lib/supabase';
import BorrowerHeader from '../components/layout/BorrowerHeader';
import ComplianceFooter from '../components/common/ComplianceFooter';
import { US_STATES } from '../data/usStates';
import {
  LENDER_PRODUCT_TYPES, LENDER_CHANNELS, VOLUME_BUCKETS,
} from '../data/lenderProductTypes';

// Palette aligned with the Owen readiness experience and the redesigned
// WelcomePage (warm off-whites + the lk indigo system).
const COLORS = {
  ink: '#18181b',
  ink2: '#3f3f46',
  ink3: '#71717a',
  ink4: '#a1a1aa',
  rule: '#eceae5',
  bg: '#faf9f7',
  brand: '#4f46e5',
  brandSoft: '#eef2ff',
  brandDark: '#312e81',
  good: '#059669',
  goodSoft: '#ecfdf5',
  warn: '#d97706',
  warnSoft: '#fffbeb',
  bad: '#e11d48',
  badSoft: '#fff1f2',
  accent: '#7c3aed',
};

const LenderMarketplaceLanding = () => {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ backgroundColor: 'white', color: COLORS.ink, minHeight: '100vh' }}>
      <Hero />
      <Problem />
      <ValueProps />
      <HowItWorks />
      <Pricing />
      <Comparison />
      <FAQ />
      <SignupForm />

      <div style={{ padding: '32px 16px', textAlign: 'center', color: COLORS.ink3, fontSize: 13 }}>
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          style={{ color: COLORS.ink3, textDecoration: 'none' }}
        >
          ← Back to LoanKnow home
        </a>
      </div>

      <ComplianceFooter />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────
const Hero = () => (
  <section style={{
    position: 'relative',
    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #3730a3 100%)',
    color: 'white', overflow: 'hidden',
  }}>
    <BorrowerHeader variant="transparent" showSignIn={false} />

    {/* Decorative gradient orbs */}
    <div aria-hidden style={{
      position: 'absolute', top: -120, right: -120, width: 380, height: 380, borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(99,102,241,0.55) 0%, transparent 70%)', filter: 'blur(20px)',
    }} />
    <div aria-hidden style={{
      position: 'absolute', bottom: -100, left: -100, width: 320, height: 320, borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(147,197,253,0.35) 0%, transparent 70%)', filter: 'blur(20px)',
    }} />

    <div style={{
      maxWidth: 1100, margin: '0 auto', padding: '80px 24px 120px',
      position: 'relative', zIndex: 1,
    }}>
      <div style={{ maxWidth: 820, margin: '0 auto', textAlign: 'center' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '6px 14px', borderRadius: 999,
          backgroundColor: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.2)',
          fontSize: 13, fontWeight: 500, color: '#a5b4fc',
          marginBottom: 28,
        }}>
          <Sparkles size={14} /> For Lenders & Loan Officers
        </div>

        <h1 style={{
          fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
          fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.025em',
          margin: 0, marginBottom: 24, color: 'white',
        }}>
          Get pre-underwritten leads,<br />
          <span style={{
            background: 'linear-gradient(90deg, #a5b4fc 0%, #c4b5fd 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            not raw inquiries.
          </span>
        </h1>

        <p style={{
          fontSize: 'clamp(1.05rem, 2vw, 1.25rem)',
          lineHeight: 1.55, opacity: 0.9,
          maxWidth: 700, margin: '0 auto 40px',
        }}>
          LoanKnow borrowers arrive at your inbox with a Mortgage Readiness Score, the right
          program already matched, Owen's pre-underwriting verdict, and a complete document
          checklist. No cold leads. No lookups. Just borrowers ready to close.
        </p>

        <div style={{
          display: 'flex', gap: 14, justifyContent: 'center',
          flexWrap: 'wrap', marginBottom: 32,
        }}>
          <button
            onClick={() => document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              backgroundColor: 'white', color: COLORS.brandDark,
              padding: '17px 36px', fontSize: 16, fontWeight: 700,
              borderRadius: 12, border: 'none', cursor: 'pointer',
              boxShadow: '0 8px 30px rgba(255,255,255,0.18)',
            }}
          >
            Apply to join the marketplace <ArrowRight size={18} />
          </button>
          <button
            onClick={() => document.getElementById('how-it-works-lender')?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              backgroundColor: 'transparent', color: 'white',
              padding: '17px 28px', fontSize: 15, fontWeight: 600,
              borderRadius: 12, border: '1px solid rgba(255,255,255,0.4)',
              cursor: 'pointer',
            }}
          >
            See how it works
          </button>
        </div>

        <div style={{
          display: 'flex', gap: 28, justifyContent: 'center', flexWrap: 'wrap',
          opacity: 0.7, fontSize: 13,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle size={14} color="#86efac" /> No upfront cost
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle size={14} color="#86efac" /> Pay per delivered package
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle size={14} color="#86efac" /> Cancel anytime
          </span>
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// Problem
// ─────────────────────────────────────────────────────────────
const Problem = () => {
  const pains = [
    {
      icon: <AlertCircle size={22} />,
      title: 'You\'re paying for tire-kickers',
      body: 'Traditional lead gen sells you a name and a phone number — you do the qualification, the credit pull, and the program education yourself.',
    },
    {
      icon: <X size={22} />,
      title: 'The same lead goes to 5 of you',
      body: 'You\'re racing the rest of the panel to be first to call. The borrower gets blown up; you get a 20% contact rate at best.',
    },
    {
      icon: <Users size={22} />,
      title: 'Borrowers don\'t know what they want',
      body: 'They want "the lowest rate" because that\'s the only metric they understand. You spend hours educating, then they ghost.',
    },
    {
      icon: <TrendingUp size={22} />,
      title: 'Your CPL keeps going up',
      body: 'Lead aggregators raise prices every quarter. Your conversion stays flat. The math gets worse every year.',
    },
  ];
  return (
    <section style={{ padding: '96px 24px', backgroundColor: 'white' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <SectionEyebrow>The current lead-gen game is broken</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, color: COLORS.ink,
          textAlign: 'center', maxWidth: 740, margin: '8px auto 18px',
        }}>
          You shouldn't have to pay for the privilege of doing free underwriting.
        </h2>
        <p style={{
          fontSize: 17, color: COLORS.ink2, lineHeight: 1.6,
          textAlign: 'center', maxWidth: 660, margin: '0 auto 56px',
        }}>
          Most lender lead products dump unqualified inquiries into your funnel and leave
          you to sort it out. Then they raise prices and sell the same lead to your competitors.
        </p>
        <div style={{
          display: 'grid', gap: 20,
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        }}>
          {pains.map((p) => (
            <div key={p.title} style={{
              padding: 24, borderRadius: 12, backgroundColor: COLORS.bg,
              border: `1px solid ${COLORS.rule}`,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                backgroundColor: COLORS.badSoft, color: COLORS.bad,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 14,
              }}>{p.icon}</div>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: COLORS.ink }}>{p.title}</h3>
              <p style={{ fontSize: 14, color: COLORS.ink2, lineHeight: 1.55, margin: 0 }}>{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Value props (the meat of the pitch)
// ─────────────────────────────────────────────────────────────
const ValueProps = () => {
  const benefits = [
    {
      icon: <FileText size={26} />,
      title: 'Enriched files, not raw leads',
      body: 'Each borrower arrives with a Mortgage Readiness Score, recommended program, Owen\'s deterministic eligibility findings, and as much of the URLA as they completed — typically credit range, income, employment, assets, and property details. You see the enrichment level upfront and pick up where the borrower left off.',
    },
    {
      icon: <Target size={26} />,
      title: 'Match-fit by design',
      body: 'You only see borrowers in your licensed states, with the products you support, and inside the programs you actually fund. No guessing. No wasted contacts.',
    },
    {
      icon: <Shield size={26} />,
      title: 'No race, no auction',
      body: 'The borrower picks who to share their file with — you, their existing LO, their realtor, or all three. You earn the conversation by being a good fit, not by being fastest on the dialer.',
    },
    {
      icon: <Award size={26} />,
      title: 'Higher contact, higher conversion',
      body: 'Borrowers chose to send the file. They expect your call. Our internal benchmark is 70%+ contact rate vs. industry standard 15-25% on shared leads.',
    },
    {
      icon: <Zap size={26} />,
      title: 'Faster from intro to clear-to-close',
      body: 'Owen\'s pre-underwriting catches the conditions up front. By the time the file hits your LOS, the borrower already knows what to gather. Pull-through goes up.',
    },
    {
      icon: <TrendingUp size={26} />,
      title: 'Pay only for what you receive',
      body: 'No monthly subscription. No minimums. You pay a delivery fee per package, on a sliding scale tied to how enriched the borrower\'s file is — pay less for a profile-only intro, more for a fully prepared, Owen-verdict file. You always know what you\'re getting before you pay.',
    },
  ];

  return (
    <section style={{ padding: '96px 24px', backgroundColor: COLORS.bg }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <SectionEyebrow color={COLORS.brand}>What you get</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, color: COLORS.ink,
          textAlign: 'center', maxWidth: 720, margin: '8px auto 18px',
        }}>
          Borrowers who already know what they want — and chose you.
        </h2>
        <p style={{
          fontSize: 17, color: COLORS.ink2, lineHeight: 1.6,
          textAlign: 'center', maxWidth: 640, margin: '0 auto 56px',
        }}>
          LoanKnow flips the model. Borrowers do the prep work with Owen, then forward
          their file to you. You pick up where the heavy lifting left off.
        </p>

        <div style={{
          display: 'grid', gap: 20,
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        }}>
          {benefits.map((b) => (
            <div key={b.title} style={{
              padding: 28, borderRadius: 14, backgroundColor: 'white',
              border: `1px solid ${COLORS.rule}`,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                backgroundColor: COLORS.brandSoft, color: COLORS.brand,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 18,
              }}>{b.icon}</div>
              <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: COLORS.ink }}>{b.title}</h3>
              <p style={{ fontSize: 14, color: COLORS.ink2, lineHeight: 1.6, margin: 0 }}>{b.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// How it works (lender-side)
// ─────────────────────────────────────────────────────────────
const HowItWorks = () => {
  const steps = [
    {
      num: '01',
      title: 'Tell us your footprint',
      body: 'Apply with the states you\'re licensed in, the products you fund (Conv, FHA, VA, USDA, Non-QM), your channels, and your monthly volume. Approval typically takes 2-3 business days.',
    },
    {
      num: '02',
      title: 'Borrowers forward their files to you',
      body: 'Match-fit borrowers see your branding when their LoanKnow packet is ready. They click "Forward to my Loan Officer" — and the file (score + program match + Owen\'s verdict + whatever URLA detail and docs they completed) lands in your inbox and dashboard.',
    },
    {
      num: '03',
      title: 'Run your normal process — faster',
      body: 'Pull credit, run AUS, lock the rate, and close. Owen\'s findings already mapped the path; you just verify and execute.',
    },
  ];

  return (
    <section id="how-it-works-lender" style={{ padding: '96px 24px', backgroundColor: 'white' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <SectionEyebrow>Three steps to your first deal</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, color: COLORS.ink,
          textAlign: 'center', maxWidth: 700, margin: '8px auto 56px',
        }}>
          From application to active marketplace member.
        </h2>

        <div style={{ display: 'grid', gap: 20 }}>
          {steps.map((s) => (
            <div key={s.num} style={{
              display: 'grid', gridTemplateColumns: '90px 1fr',
              gap: 24, alignItems: 'flex-start',
              padding: 28, borderRadius: 14,
              backgroundColor: COLORS.bg,
              border: `1px solid ${COLORS.rule}`,
            }}>
              <div style={{
                fontSize: 38, fontWeight: 800, lineHeight: 1,
                color: COLORS.brand, fontVariantNumeric: 'tabular-nums',
              }}>
                {s.num}
              </div>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: COLORS.ink }}>
                  {s.title}
                </h3>
                <p style={{ fontSize: 15, color: COLORS.ink2, lineHeight: 1.6, margin: 0 }}>
                  {s.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Pricing
// ─────────────────────────────────────────────────────────────
const Pricing = () => {
  // Three tiers map directly to the depth of the borrower's prep work.
  // Exact dollars are intentionally not shown publicly — we surface them
  // during onboarding once we've verified the lender's licensing footprint.
  const tiers = [
    {
      name: 'Profile',
      sub: 'Borrower completed the assessment',
      bullets: [
        'Verified contact info + property state',
        'Mortgage Readiness Score',
        'Recommended loan program & rationale',
        'Stated credit, income, and down payment range',
      ],
      badge: 'Lowest fee',
      badgeColor: COLORS.brand,
      badgeSoft: COLORS.brandSoft,
    },
    {
      name: 'Profile + URLA',
      sub: 'Borrower started the application',
      bullets: [
        'Everything in Profile, plus…',
        'Whatever URLA sections the borrower completed',
        'Property details, employment, assets',
        'Live readiness breakdown by category',
      ],
      badge: 'Mid tier',
      badgeColor: COLORS.warn,
      badgeSoft: COLORS.warnSoft,
      featured: true,
    },
    {
      name: 'Pre-underwritten',
      sub: 'Owen ran his deterministic verdict',
      bullets: [
        'Everything in Profile + URLA, plus…',
        'Owen\'s pre-underwriting findings (pass / condition / fail)',
        'Document checklist & any uploaded docs',
        'Owen\'s narrative verdict to share with your team',
      ],
      badge: 'Top tier',
      badgeColor: COLORS.good,
      badgeSoft: COLORS.goodSoft,
    },
  ];

  return (
    <section style={{ padding: '96px 24px', backgroundColor: COLORS.bg }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <SectionEyebrow color={COLORS.good}>Pricing</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, color: COLORS.ink, margin: '8px auto 18px',
          textAlign: 'center', maxWidth: 720,
        }}>
          Pay per delivered package, on a sliding scale.
        </h2>
        <p style={{
          fontSize: 17, color: COLORS.ink2, lineHeight: 1.6,
          textAlign: 'center', maxWidth: 720, margin: '0 auto 48px',
        }}>
          No monthly subscription. No per-click charges. No annual commitment. You pay a
          delivery fee each time a borrower forwards a file to you — and the fee scales with
          how enriched that file is. The deeper the borrower's prep, the more you pay; the
          lighter the file, the lighter the fee.
        </p>

        {/* Tier cards */}
        <div style={{
          display: 'grid', gap: 20,
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          maxWidth: 1000, margin: '0 auto 40px',
        }}>
          {tiers.map((t) => (
            <div
              key={t.name}
              style={{
                position: 'relative',
                backgroundColor: 'white', borderRadius: 16,
                padding: 28, paddingTop: 32,
                border: t.featured ? `2px solid ${COLORS.brand}` : `1px solid ${COLORS.rule}`,
                boxShadow: t.featured ? '0 12px 36px rgba(79,70,229,0.14)' : '0 4px 16px rgba(0,0,0,0.04)',
                transform: t.featured ? 'translateY(-6px)' : 'none',
              }}
            >
              <div style={{
                position: 'absolute', top: -10, left: 20,
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', borderRadius: 999,
                backgroundColor: t.badgeSoft, color: t.badgeColor,
                fontSize: 11, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.6,
              }}>
                {t.badge}
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: COLORS.ink, margin: '0 0 4px' }}>
                {t.name}
              </h3>
              <div style={{ fontSize: 13, color: COLORS.ink3, marginBottom: 18 }}>{t.sub}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {t.bullets.map((b) => (
                  <div key={b} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: COLORS.ink2, lineHeight: 1.5 }}>
                    <CheckCircle size={14} color={t.badgeColor} style={{ marginTop: 2, flexShrink: 0 }} />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Founding cohort callout */}
        <div style={{
          maxWidth: 720, margin: '0 auto',
          backgroundColor: 'white', borderRadius: 18, padding: 32,
          border: `1px solid ${COLORS.rule}`,
          boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
          textAlign: 'center',
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 12px', borderRadius: 999,
            backgroundColor: COLORS.goodSoft, color: COLORS.good,
            fontSize: 12, fontWeight: 700,
            marginBottom: 14, textTransform: 'uppercase', letterSpacing: 0.5,
          }}>
            <Sparkles size={12} /> Founding Lender Cohort
          </div>
          <h3 style={{
            fontSize: 22, fontWeight: 800, color: COLORS.ink,
            margin: '0 0 12px', lineHeight: 1.25,
          }}>
            Reduced sliding-scale rates for your first 12 months.
          </h3>
          <p style={{
            fontSize: 15, color: COLORS.ink2, lineHeight: 1.6,
            margin: '0 auto', maxWidth: 540,
          }}>
            Founding lenders get the entire sliding scale discounted across all three tiers
            for 12 months from onboarding. After that you roll to standard marketplace
            pricing — but you keep the founding-cohort badge in the borrower selector
            for as long as you stay active.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center', marginTop: 20, fontSize: 13, color: COLORS.ink3 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <CheckCircle size={14} color={COLORS.good} /> $0 to onboard
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <CheckCircle size={14} color={COLORS.good} /> Cancel anytime
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <CheckCircle size={14} color={COLORS.good} /> No package floor
            </span>
          </div>
          <p style={{
            fontSize: 12, color: COLORS.ink4, marginTop: 22, marginBottom: 0,
            fontStyle: 'italic',
          }}>
            Specific tier dollar amounts are shared during onboarding once we've verified
            your licensing footprint. Founding-cohort enrollment closes when we hit our cap.
          </p>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Comparison table
// ─────────────────────────────────────────────────────────────
const Comparison = () => {
  const rows = [
    { trad: 'You pay per lead, even if they ghost', us: 'You pay per delivered package, on a sliding scale' },
    { trad: 'Same lead sold to 4–6 other lenders', us: 'Borrower picks who they share the file with' },
    { trad: 'Cold inquiry — name + phone + maybe email', us: 'Score, program match, URLA progress, doc list, Owen\'s verdict' },
    { trad: '15–25% contact rate', us: '70%+ contact rate (borrower expects your call)' },
    { trad: 'You do the qualification work for free', us: 'Owen has already done the heavy lifting' },
    { trad: 'Lead price keeps going up every year', us: 'Founding-cohort sliding-scale discount for 12 months' },
  ];
  return (
    <section style={{ padding: '96px 24px', backgroundColor: 'white' }}>
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <SectionEyebrow>Side by side</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, color: COLORS.ink,
          textAlign: 'center', maxWidth: 700, margin: '8px auto 56px',
        }}>
          Lead aggregators vs. LoanKnow.
        </h2>
        <div style={{
          backgroundColor: 'white', borderRadius: 14,
          border: `1px solid ${COLORS.rule}`, overflow: 'hidden',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            backgroundColor: COLORS.brandDark, color: 'white',
          }}>
            <div style={{ padding: '18px 24px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, opacity: 0.7 }}>
              Lead aggregator
            </div>
            <div style={{ padding: '18px 24px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#a5b4fc' }}>
              LoanKnow marketplace
            </div>
          </div>
          {rows.map((r, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              borderTop: i === 0 ? 'none' : `1px solid ${COLORS.rule}`,
            }}>
              <div style={{
                padding: '18px 24px', fontSize: 14, color: COLORS.ink3,
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <X size={16} color={COLORS.bad} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>{r.trad}</span>
              </div>
              <div style={{
                padding: '18px 24px', fontSize: 14, color: COLORS.ink, fontWeight: 500,
                backgroundColor: '#fcfcff',
                display: 'flex', alignItems: 'flex-start', gap: 10,
                borderLeft: `1px solid ${COLORS.rule}`,
              }}>
                <CheckCircle size={16} color={COLORS.good} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>{r.us}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// FAQ
// ─────────────────────────────────────────────────────────────
const FAQ = () => {
  const items = [
    {
      q: 'How do I get approved to receive files?',
      a: 'Submit the form below with your states, products, channels, and NMLS ID. We verify licensing and channel approvals (typically 2–3 business days), then you\'re live in the marketplace.',
    },
    {
      q: 'How does the sliding-scale pricing actually work?',
      a: 'Every package has an enrichment level — Profile, Profile + URLA, or Pre-underwritten — and each level has its own delivery fee. You see the level and the fee before you accept the package, so you can opt in only to what you want. Founding-cohort lenders get the entire scale discounted for 12 months.',
    },
    {
      q: 'How "complete" is the URLA when a borrower forwards a file?',
      a: 'Honestly, it varies. Some borrowers stop after the assessment (Profile tier), others fill out their employment and assets but skip a section, and others go all the way through Owen\'s pre-underwriting. We label every package with exactly what the borrower completed so you know what you\'re getting before you pay.',
    },
    {
      q: 'Can I cap how many forwards I receive per month?',
      a: 'Yes. After onboarding, you set monthly volume caps per enrichment tier and can pause/resume your account at any time from the lender dashboard.',
    },
    {
      q: 'Are these exclusive forwards?',
      a: 'A LoanKnow borrower picks who they want to share their file with. They can pick you only, or they can pick you alongside one or two others — but never the 4–6 lenders that aggregator leads get sold to.',
    },
    {
      q: 'Do borrowers see my pricing or rate sheet?',
      a: 'No. The marketplace surfaces your brand and any positioning content you provide. Pricing happens in your normal sales conversation — LoanKnow doesn\'t commodify rates.',
    },
    {
      q: 'What about TILA-RESPA, ECOA, and fair-lending compliance?',
      a: 'You retain full control of disclosures, credit pulls, AUS submission, locking, and closing. LoanKnow is a borrower preparation platform — we\'re not a lender, and we don\'t make credit decisions.',
    },
    {
      q: 'How do you protect borrower data?',
      a: 'All borrower data is encrypted at rest and in transit, never sold, and only released to a lender on the borrower\'s explicit forward action. We\'re GLBA-aligned and SOC 2 compliant.',
    },
  ];
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section style={{ padding: '96px 24px', backgroundColor: COLORS.bg }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <SectionEyebrow>Common questions</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, color: COLORS.ink,
          textAlign: 'center', maxWidth: 600, margin: '8px auto 40px',
        }}>
          What lenders ask before joining.
        </h2>
        <div style={{
          backgroundColor: 'white', borderRadius: 12,
          border: `1px solid ${COLORS.rule}`, overflow: 'hidden',
        }}>
          {items.map((item, i) => {
            const open = openIdx === i;
            return (
              <div key={i} style={{ borderTop: i === 0 ? 'none' : `1px solid ${COLORS.rule}` }}>
                <button
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', gap: 16,
                    padding: '20px 24px', background: 'none', border: 'none',
                    fontSize: 16, fontWeight: 600, color: COLORS.ink,
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <span>{item.q}</span>
                  {open ? <ChevronUp size={18} color={COLORS.ink3} /> : <ChevronDown size={18} color={COLORS.ink3} />}
                </button>
                {open && (
                  <div style={{
                    padding: '0 24px 20px', fontSize: 15,
                    color: COLORS.ink2, lineHeight: 1.65,
                  }}>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Signup form
// ─────────────────────────────────────────────────────────────
const SignupForm = () => {
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [nmlsId, setNmlsId] = useState('');
  const [statesNationwide, setStatesNationwide] = useState(false);
  const [statesCovered, setStatesCovered] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [channels, setChannels] = useState([]);
  const [monthlyVolume, setMonthlyVolume] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [stateFilter, setStateFilter] = useState('');

  const filteredStates = useMemo(() => {
    const q = stateFilter.trim().toLowerCase();
    if (!q) return US_STATES;
    return US_STATES.filter((s) =>
      s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q)
    );
  }, [stateFilter]);

  const toggleArrayValue = (arr, setArr, value) => {
    setArr(arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]);
  };

  const isValid =
    companyName.trim().length >= 2 &&
    contactName.trim().length >= 2 &&
    /^\S+@\S+\.\S+$/.test(contactEmail) &&
    productTypes.length > 0 &&
    (statesNationwide || statesCovered.length > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;

    if (!isSupabaseConfigured) {
      setError(friendlyErrorMessage());
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const url = new URL(window.location.href);
      const payload = {
        company_name:    companyName.trim(),
        contact_name:    contactName.trim(),
        contact_email:   contactEmail.trim().toLowerCase(),
        contact_phone:   contactPhone.trim() || null,
        company_website: companyWebsite.trim() || null,
        nmls_id:         nmlsId.trim() || null,
        states_covered:  statesNationwide ? US_STATES.map((s) => s.code) : statesCovered,
        product_types:   productTypes,
        channels:        channels,
        monthly_volume:  monthlyVolume || null,
        notes:           notes.trim() || null,
        utm_source:      url.searchParams.get('utm_source'),
        utm_medium:      url.searchParams.get('utm_medium'),
        utm_campaign:    url.searchParams.get('utm_campaign'),
      };

      const { error: dbError } = await supabase
        .from('lender_marketplace_signups')
        .insert([payload]);

      if (dbError) {
        if (dbError.code === '23505' || /duplicate/i.test(dbError.message || '')) {
          setSuccess(true);
          fireLenderConfirmation(payload);
          return;
        }
        throw dbError;
      }
      setSuccess(true);
      fireLenderConfirmation(payload);
    } catch (err) {
      console.error('Lender signup failed', err);
      setError(friendlyErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Fire-and-forget; Edge Function handles idempotency.
  const fireLenderConfirmation = (payload) => {
    supabase.functions
      .invoke('send-signup-confirmation', {
        body: {
          kind: 'lender',
          companyName: payload.company_name,
          contactName: payload.contact_name,
          contactEmail: payload.contact_email,
          productTypes: payload.product_types,
          statesCovered: payload.states_covered,
          source: 'lender_marketplace_landing',
        },
      })
      .then(({ error: fnError }) => {
        if (fnError) console.warn('Lender confirmation email failed:', fnError.message);
      })
      .catch((e) => console.warn('Lender confirmation email exception:', e));
  };

  if (success) {
    return (
      <section id="signup" style={{ padding: '96px 24px 64px', backgroundColor: 'white' }}>
        <div style={{
          maxWidth: 640, margin: '0 auto', textAlign: 'center',
          backgroundColor: COLORS.goodSoft, borderRadius: 20, padding: '56px 32px',
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            backgroundColor: 'white',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 20,
          }}>
            <CheckCircle size={36} color={COLORS.good} />
          </div>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: COLORS.ink, marginBottom: 12, lineHeight: 1.2 }}>
            We've got your application.
          </h2>
          <p style={{ fontSize: 16, color: COLORS.ink2, lineHeight: 1.6, marginBottom: 0, maxWidth: 460, margin: '0 auto' }}>
            Our partnerships team will review your details and reach out within 2–3 business days
            to schedule a quick onboarding call. Welcome to LoanKnow.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="signup" style={{ padding: '96px 24px 64px', backgroundColor: 'white' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <SectionEyebrow color={COLORS.brand}>Apply to join</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, color: COLORS.ink,
          textAlign: 'center', maxWidth: 600, margin: '8px auto 18px',
        }}>
          Join the founding lender cohort.
        </h2>
        <p style={{
          fontSize: 16, color: COLORS.ink2, lineHeight: 1.6,
          textAlign: 'center', maxWidth: 560, margin: '0 auto 40px',
        }}>
          Tell us your footprint. We'll be in touch within 2–3 business days to verify
          your licensing and onboard you to the marketplace.
        </p>

        <form
          onSubmit={handleSubmit}
          noValidate
          style={{
            backgroundColor: COLORS.bg, padding: '36px',
            borderRadius: 16, border: `1px solid ${COLORS.rule}`,
          }}
        >
          {/* Contact section */}
          <FieldGroup title="Company & contact">
            <Row>
              <Field label="Company name *" full>
                <input
                  type="text" value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Mortgage, LLC"
                  style={inputStyle}
                />
              </Field>
            </Row>
            <Row>
              <Field label="Your name *">
                <input
                  type="text" value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Jane Smith"
                  style={inputStyle}
                />
              </Field>
              <Field label="Title / role">
                <input
                  type="text" value={notes && notes.startsWith('Title:') ? notes.replace('Title: ', '') : ''}
                  onChange={(e) => setNotes(e.target.value ? `Title: ${e.target.value}` : '')}
                  placeholder="VP Sales, Branch Manager…"
                  style={inputStyle}
                />
              </Field>
            </Row>
            <Row>
              <Field label="Work email *">
                <input
                  type="email" value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="jane@acmemortgage.com"
                  style={inputStyle}
                />
              </Field>
              <Field label="Phone">
                <input
                  type="tel" value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  style={inputStyle}
                />
              </Field>
            </Row>
            <Row>
              <Field label="Company website">
                <input
                  type="url" value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                  placeholder="https://acmemortgage.com"
                  style={inputStyle}
                />
              </Field>
              <Field label="NMLS ID (company or LO)">
                <input
                  type="text" value={nmlsId}
                  onChange={(e) => setNmlsId(e.target.value)}
                  placeholder="123456"
                  style={inputStyle}
                />
              </Field>
            </Row>
          </FieldGroup>

          {/* Coverage */}
          <FieldGroup title="States you're licensed in *">
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '8px 14px', borderRadius: 999,
              backgroundColor: statesNationwide ? COLORS.brand : 'white',
              color: statesNationwide ? 'white' : COLORS.ink2,
              border: `1px solid ${statesNationwide ? COLORS.brand : COLORS.rule}`,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              marginBottom: 14,
            }}>
              <input
                type="checkbox" checked={statesNationwide}
                onChange={(e) => setStatesNationwide(e.target.checked)}
                style={{ display: 'none' }}
              />
              <MapPin size={14} />
              Licensed in all 50 states (nationwide)
            </label>

            {!statesNationwide && (
              <>
                <input
                  type="text" placeholder="Filter states…"
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  style={{ ...inputStyle, marginBottom: 12 }}
                />
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  gap: 6, maxHeight: 280, overflowY: 'auto',
                  padding: 12, borderRadius: 10,
                  backgroundColor: 'white', border: `1px solid ${COLORS.rule}`,
                }}>
                  {filteredStates.map((s) => {
                    const checked = statesCovered.includes(s.code);
                    return (
                      <label key={s.code} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 10px', borderRadius: 6,
                        backgroundColor: checked ? COLORS.brandSoft : 'transparent',
                        fontSize: 13, color: COLORS.ink2,
                        cursor: 'pointer',
                      }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleArrayValue(statesCovered, setStatesCovered, s.code)}
                          style={{ accentColor: COLORS.brand }}
                        />
                        {s.name}
                      </label>
                    );
                  })}
                </div>
                <div style={{ fontSize: 12, color: COLORS.ink3, marginTop: 8 }}>
                  {statesCovered.length} state{statesCovered.length === 1 ? '' : 's'} selected
                </div>
              </>
            )}
          </FieldGroup>

          {/* Product types */}
          <FieldGroup title="Products you support * (pick all that apply)">
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              {LENDER_PRODUCT_TYPES.map((p) => {
                const checked = productTypes.includes(p.code);
                return (
                  <label
                    key={p.code}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '14px 16px', borderRadius: 10,
                      backgroundColor: checked ? COLORS.brandSoft : 'white',
                      border: `1.5px solid ${checked ? COLORS.brand : COLORS.rule}`,
                      cursor: 'pointer', transition: 'all 120ms ease',
                    }}
                  >
                    <input
                      type="checkbox" checked={checked}
                      onChange={() => toggleArrayValue(productTypes, setProductTypes, p.code)}
                      style={{ marginTop: 3, accentColor: COLORS.brand }}
                    />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>{p.name}</div>
                      <div style={{ fontSize: 12, color: COLORS.ink3, marginTop: 2, lineHeight: 1.4 }}>{p.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </FieldGroup>

          {/* Channels & volume */}
          <FieldGroup title="Operations">
            <Row>
              <Field label="Channel">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {LENDER_CHANNELS.map((c) => {
                    const checked = channels.includes(c.code);
                    return (
                      <label key={c.code} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 999,
                        backgroundColor: checked ? COLORS.brand : 'white',
                        color: checked ? 'white' : COLORS.ink2,
                        border: `1px solid ${checked ? COLORS.brand : COLORS.rule}`,
                        fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      }}>
                        <input
                          type="checkbox" checked={checked}
                          onChange={() => toggleArrayValue(channels, setChannels, c.code)}
                          style={{ display: 'none' }}
                        />
                        {c.name}
                      </label>
                    );
                  })}
                </div>
              </Field>
              <Field label="Monthly funded volume">
                <select
                  value={monthlyVolume}
                  onChange={(e) => setMonthlyVolume(e.target.value)}
                  style={{ ...inputStyle, paddingRight: 32, appearance: 'none' }}
                >
                  <option value="">Select…</option>
                  {VOLUME_BUCKETS.map((b) => (
                    <option key={b.code} value={b.code}>{b.label}</option>
                  ))}
                </select>
              </Field>
            </Row>
          </FieldGroup>

          {error && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              padding: '12px 14px', borderRadius: 8,
              backgroundColor: COLORS.badSoft, color: COLORS.bad,
              fontSize: 14, lineHeight: 1.4, marginBottom: 16,
            }}>
              <AlertCircle size={16} style={{ marginTop: 1, flexShrink: 0 }} />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={!isValid || submitting}
            style={{
              width: '100%', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', gap: 8,
              backgroundColor: isValid && !submitting ? COLORS.brand : '#a3a3a3',
              color: 'white', padding: '16px 28px',
              fontSize: 16, fontWeight: 600,
              borderRadius: 12, border: 'none',
              boxShadow: isValid && !submitting ? '0 8px 24px -8px rgba(79,70,229,0.45)' : 'none',
              cursor: isValid && !submitting ? 'pointer' : 'not-allowed',
              marginTop: 8,
            }}
          >
            {submitting ? 'Submitting…' : 'Apply to the Founding Lender Cohort'}
            {!submitting && <ArrowRight size={18} />}
          </button>

          <p style={{
            fontSize: 12, color: COLORS.ink3, textAlign: 'center',
            marginTop: 16, marginBottom: 0, lineHeight: 1.5,
          }}>
            Submitting this form is not a commitment. We review every application and reach out
            within 2–3 business days. * Required fields.
          </p>
        </form>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const SectionEyebrow = ({ children, color }) => (
  <div style={{
    fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 1.2,
    color: color || COLORS.brand,
    textAlign: 'center', marginBottom: 4,
  }}>
    {children}
  </div>
);

const FieldGroup = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <h3 style={{
      fontSize: 13, fontWeight: 700, color: COLORS.ink,
      marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.6,
    }}>
      {title}
    </h3>
    {children}
  </div>
);

const Row = ({ children }) => (
  <div style={{
    display: 'grid', gap: 12,
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    marginBottom: 12,
  }}>
    {children}
  </div>
);

const Field = ({ label, children, full = false }) => (
  <label style={{ display: 'block', gridColumn: full ? '1 / -1' : 'auto' }}>
    <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.ink2, marginBottom: 6 }}>{label}</div>
    {children}
  </label>
);

const inputStyle = {
  width: '100%',
  padding: '11px 14px',
  fontSize: 14,
  border: `1px solid ${COLORS.rule}`,
  borderRadius: 10,
  backgroundColor: 'white',
  color: COLORS.ink,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

export default LenderMarketplaceLanding;
